import type { AppProps } from 'next/app';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import Head from 'next/head';
import SeoInlineHelp from '@/components/SeoInlineHelp';
import SeoClientWorkspaceShell from '@/components/SeoClientWorkspaceShell';

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) return <div style={{ padding: 24 }}><h2>Something went wrong.</h2><p>Please refresh and try again.</p></div>;
    return this.props.children;
  }
}

const topBarLinkStyle: React.CSSProperties = {
  background: 'transparent', color: '#9090b0', border: '1px solid #2a2a3a', borderRadius: 7,
  padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', textDecoration: 'none',
  whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center',
};

function AdminTeamTopBar() {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || window.location.pathname !== '/') return;

    let cancelled = false;
    let mountedSlot: HTMLElement | null = null;
    let observer: MutationObserver | null = null;
    let timeoutId: number | null = null;

    function placeTeamSlot(): boolean {
      const nav = document.querySelector('nav');
      if (!nav) return false;
      const helpButton = Array.from(nav.querySelectorAll('button')).find(button => button.textContent?.includes('Help'));
      const actions = helpButton?.parentElement;
      if (!actions || !helpButton) return false;

      let target = document.getElementById('blackbird-team-topbar-slot');
      if (!target) {
        target = document.createElement('span');
        target.id = 'blackbird-team-topbar-slot';
        target.style.display = 'contents';
        actions.insertBefore(target, helpButton);
      }
      mountedSlot = target;
      if (!cancelled) setSlot(target);
      return true;
    }

    async function mountForAdmin() {
      try {
        const response = await fetch('/api/auth/me');
        const me = response.ok ? await response.json() : null;
        if (cancelled || me?.role !== 'admin') return;

        if (placeTeamSlot()) return;
        observer = new MutationObserver(() => {
          if (placeTeamSlot()) observer?.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        timeoutId = window.setTimeout(() => observer?.disconnect(), 3000);
      } catch {
        // Keep the main Content Tool usable even if the role check fails.
      }
    }

    void mountForAdmin();
    return () => {
      cancelled = true;
      observer?.disconnect();
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      mountedSlot?.remove();
    };
  }, []);

  if (!slot) return null;
  return createPortal(
    <>
      <a href="/review-history" aria-label="Review history" style={topBarLinkStyle}>📋 Reviews</a>
      <a href="/team" aria-label="Manage team" style={topBarLinkStyle}>👥 Team</a>
    </>,
    slot,
  );
}

function SeoOpportunitiesTopBar() {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || window.location.pathname !== '/') return;

    let cancelled = false;
    let mountedSlot: HTMLElement | null = null;
    let observer: MutationObserver | null = null;
    let timeoutId: number | null = null;

    function placeSeoSlot(): boolean {
      const nav = document.querySelector('nav');
      if (!nav) return false;
      const helpButton = Array.from(nav.querySelectorAll('button')).find(button => button.textContent?.includes('Help'));
      const actions = helpButton?.parentElement;
      if (!actions || !helpButton) return false;
      let target = document.getElementById('blackbird-seo-topbar-slot');
      if (!target) {
        target = document.createElement('span');
        target.id = 'blackbird-seo-topbar-slot';
        target.style.display = 'contents';
        actions.insertBefore(target, helpButton);
      }
      mountedSlot = target;
      if (!cancelled) setSlot(target);
      return true;
    }

    async function mountForMember() {
      try {
        const response = await fetch('/api/auth/me');
        const me = response.ok ? await response.json() : null;
        if (cancelled || !me?.username) return;
        if (placeSeoSlot()) return;
        observer = new MutationObserver(() => {
          if (placeSeoSlot()) observer?.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        timeoutId = window.setTimeout(() => observer?.disconnect(), 3000);
      } catch {
        // Keep the main Content Tool usable even if auth lookup is unavailable.
      }
    }

    void mountForMember();
    return () => {
      cancelled = true;
      observer?.disconnect();
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      mountedSlot?.remove();
    };
  }, []);

  if (!slot) return null;
  return createPortal(
    <>
      <a href="/seo-work-queue" aria-label="SEO work queue" style={topBarLinkStyle}>📊 Work Queue</a>
      <a href="/seo" aria-label="SEO workspace" style={topBarLinkStyle}>🎯 SEO</a>
    </>,
    slot,
  );
}

function SeoWorkspaceBackLinkGuard() {
  useEffect(() => {
    if (typeof window === 'undefined' || window.location.pathname !== '/seo') return;

    let observer: MutationObserver | null = null;
    let timeoutId: number | null = null;

    function replaceLegacyBackLink(): boolean {
      const legacy = document.querySelector<HTMLAnchorElement>('a[href="/?tab=audit"]');
      if (!legacy) return false;
      legacy.href = '/seo-work-queue';
      legacy.textContent = '← Work Queue';
      return true;
    }

    if (!replaceLegacyBackLink()) {
      observer = new MutationObserver(() => {
        if (replaceLegacyBackLink()) observer?.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      timeoutId = window.setTimeout(() => observer?.disconnect(), 3000);
    }

    return () => {
      observer?.disconnect();
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, []);

  return null;
}

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const clientId = typeof router.query.clientId === 'string' ? router.query.clientId : '';
  const selectedClientSeo = router.pathname === '/seo' && Boolean(clientId);
  const page = <Component {...pageProps} />;

  return (
    <ErrorBoundary>
      <Head>
        <title>Blackbird Content Engine</title>
        <link rel="icon" href="/favicon.png" type="image/png" />
        <link rel="apple-touch-icon" href="/favicon.png" />
        <meta name="theme-color" content="#0a0a0f" />
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      {selectedClientSeo ? <SeoClientWorkspaceShell clientId={clientId}>{page}</SeoClientWorkspaceShell> : page}
      <SeoInlineHelp />
      <SeoWorkspaceBackLinkGuard />
      <SeoOpportunitiesTopBar />
      <AdminTeamTopBar />
    </ErrorBoundary>
  );
}

export default App;